import { useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useNavigate } from 'react-router'
import { useTranslate } from '@repo/unicity'
import { useLogin } from '@/core/auth/useLogin'
import { ReCaptchaV2Modal } from '@/core/recaptcha/recaptcha-v2-modal.component'
import { useRecaptchaGate } from '@/core/recaptcha/use-recaptcha-gate.hook'
import OpenPasswordIcon from '@/core/assets/OpenPassword.svg?react'
import ClosePasswordIcon from '@/core/assets/ClosePassword.svg?react'

export interface LoginModalProps {
    open: boolean
    onClose: () => void
    /** Called after a successful login (modal already closed). */
    onSuccess?: () => void
}

export const LoginModal = ({ open, onClose, onSuccess }: LoginModalProps) => {
    const { translate } = useTranslate()
    const navigate = useNavigate()

    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const { isVerifying: recaptchaVerifying, runWithRecaptcha, modalProps: recaptchaModalProps } = useRecaptchaGate()

    const resetForm = () => {
        setUsername('')
        setPassword('')
        setShowPassword(false)
    }

    const { login, isLoading, error } = useLogin({
        onSuccess: () => {
            resetForm()
            onClose()
            onSuccess?.()
        }
    })
    const isSubmitting = isLoading || recaptchaVerifying

    const handleClose = () => {
        if (isSubmitting) return
        resetForm()
        onClose()
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!username.trim() || !password) return
        await runWithRecaptcha('login', () => login(username, password))
    }

    const inputSx = {
        '& .MuiOutlinedInput-root': {
            height: 50,
            borderRadius: '8px',
            bgcolor: '#FFFFFF',
            fontSize: '14px',
            fontWeight: 400,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(153, 188, 223, 0.25)' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(153, 188, 223, 0.25)' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(153, 188, 223, 0.25)', borderWidth: '1px' }
        }
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: '12px', maxWidth: 340, backgroundColor: '#FFFFFF' } }}
            data-testid="login-modal-dialog">
            <ReCaptchaV2Modal {...recaptchaModalProps} />
            <Box sx={{ position: 'relative', px: { xs: 3, sm: 4 }, py: { xs: 3, sm: 4 } }} data-testid="login-modal-box-icon-button-handle-close">
                <IconButton
                    onClick={handleClose}
                    sx={{ position: 'absolute', top: 12, right: 12, color: '#003764' }}
                    data-testid="login-modal-icon-button-handle-close">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-testid="login-modal-svg">
                        <path d="M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-testid="login-modal-path-1" />
                        <path d="M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-testid="login-modal-path-2" />
                    </svg>
                </IconButton>

                <Typography sx={{ fontSize: 28, fontWeight: 700, color: '#003B6F', textAlign: 'center', mb: 3 }} data-testid="login-modal-typography-login">
                    {translate('login')}
                </Typography>

                <Box component="form" onSubmit={handleSubmit} data-testid="login-modal-box-handle-submit">
                    <Stack spacing={2.5} alignItems="center" data-testid="login-modal-stack-typography-email-or-unicity-id-1">
                        {error && (
                            <Alert severity="error" sx={{ fontSize: 13, width: '100%' }} data-testid="login-modal-alert">
                                {error}
                            </Alert>
                        )}

                        <Stack spacing={0.75} width="100%" data-testid="login-modal-stack-typography-email-or-unicity-id-2">
                            <Typography
                                variant="caption"
                                fontWeight={500}
                                color="text.secondary"
                                sx={{ pl: '5px' }}
                                data-testid="login-modal-typography-email-or-unicity-id">
                                {translate('email_or_unicity_id')}
                            </Typography>
                            <TextField
                                fullWidth
                                value={username}
                                onChange={e => setUsername(e.target.value)}
                                disabled={isSubmitting}
                                autoComplete="username"
                                autoFocus
                                size="small"
                                inputProps={{ 'aria-label': 'username', style: { fontWeight: 400 } }}
                                sx={inputSx}
                                data-testid="login-modal-text-field-1"
                            />
                        </Stack>

                        <Stack spacing={0.75} width="100%" data-testid="login-modal-stack-typography-password">
                            <Typography
                                variant="caption"
                                fontWeight={500}
                                color="text.secondary"
                                sx={{ pl: '5px' }}
                                data-testid="login-modal-typography-password">
                                {translate('password')}
                            </Typography>
                            <TextField
                                fullWidth
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                disabled={isSubmitting}
                                autoComplete="current-password"
                                size="small"
                                inputProps={{ 'aria-label': 'password', style: { fontWeight: 400 } }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end" data-testid="login-modal-input-adornment">
                                            <IconButton
                                                onClick={() => setShowPassword(v => !v)}
                                                edge="end"
                                                size="small"
                                                tabIndex={-1}
                                                sx={{ color: 'text.disabled' }}
                                                data-testid="login-modal-icon-button">
                                                {showPassword ? (
                                                    <OpenPasswordIcon width={22} height={14} data-testid="login-modal-open-password-icon" />
                                                ) : (
                                                    <ClosePasswordIcon width={22} height={12} data-testid="login-modal-close-password-icon" />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                sx={inputSx}
                                data-testid="login-modal-text-field-2"
                            />
                        </Stack>

                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting || !username.trim() || !password}
                            sx={{
                                width: '100%',
                                height: 48,
                                fontWeight: 700,
                                fontSize: '16px',
                                borderRadius: '32px',
                                bgcolor: '#1976d2',
                                boxShadow: 'none',
                                '&:hover': { bgcolor: '#1565c0', boxShadow: 'none' },
                                mt: 1
                            }}
                            data-testid="login-modal-button-submit">
                            {isSubmitting ? <CircularProgress size={20} color="inherit" data-testid="login-modal-circular-progress" /> : translate('login')}
                        </Button>

                        <Link
                            component="button"
                            type="button"
                            variant="body2"
                            onClick={() => {
                                handleClose()
                                navigate('/reset-password')
                            }}
                            sx={{
                                fontWeight: 400,
                                fontSize: '14px',
                                textDecoration: 'underline',
                                color: 'rgb(21, 56, 98)',
                                cursor: 'pointer'
                            }}
                            data-testid="login-modal-link-button">
                            {translate('forgot_password')}
                        </Link>
                    </Stack>
                </Box>
            </Box>
        </Dialog>
    )
}
